import { Box, SpaceBetween } from "@amzn/awsui-components-react";
import React from "react";
import utilStyles from "styles/utils.module.scss";
import { InfoPopover } from "components/info-popover";
import { FilePreviewProps } from "./definitions";
import {
  SUPPORTED_PREVIEW_EXTENSION_GROUPS,
  isFileTypePreviewable,
} from "./utils";

export const SupportedTypesContent = () => (
  <SpaceBetween direction="vertical" size="xxs">
    {SUPPORTED_PREVIEW_EXTENSION_GROUPS.map((group) => (
      <div key={group.label}>
        <Box fontWeight="bold" display="inline">
          {group.label}:
        </Box>{" "}
        {group.extensions.map((ext) => ext.toUpperCase()).join(", ")}
      </div>
    ))}
  </SpaceBetween>
);

const UnsupportedMessage = () => (
  <p data-testid="preview-not">
    Preview not available for this file type.
    <span data-testid="info-popover-trigger" style={{ marginLeft: "6px" }}>
      <InfoPopover content={<SupportedTypesContent />} />
    </span>
  </p>
);

export const FilePreview: React.FC<FilePreviewProps> = ({
  type,
  fileName,
  presignedUrl,
}) => {
  const getFileType = (type: string) => {
    return type.split(".").pop()?.toLowerCase();
  };

  const renderPreview = () => {
    if (!isFileTypePreviewable(type)) {
      return <UnsupportedMessage />;
    }

    const fileType = getFileType(type);

    switch (fileType) {
      case "txt":
      case "json":
      case "pdf":
        return (
          // biome-ignore lint/a11y/useIframeTitle: existing pattern
          <iframe
            src={presignedUrl}
            width="100%"
            height="900px"
            data-testid="preview-iframe"
          />
        );
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
      case "bmp":
      case "webp":
        return (
          <img
            src={presignedUrl}
            alt={fileName}
            className={utilStyles.responsiveImage}
            data-testid="preview-img"
          />
        );
      case "mp4":
      case "webm":
      case "ogg":
        return (
          // biome-ignore lint/a11y/useMediaCaption: existing pattern
          <video controls width="100%" data-testid="preview-video">
            <source
              src={presignedUrl}
              type={`video/${fileType}`}
              data-testid="video-source"
            />
            Your browser does not support the video tag.
          </video>
        );
      case "mp3":
      case "wav":
      case "aac":
      case "flac":
        return (
          // biome-ignore lint/a11y/useMediaCaption: existing pattern
          <audio controls data-testid="preview-audio">
            <source
              src={presignedUrl}
              type={`audio/${fileType}`}
              data-testid="audio-source"
            />
            Your browser does not support the audio element.
          </audio>
        );
      default:
        return <UnsupportedMessage />;
    }
  };

  return <div>{renderPreview()}</div>;
};
